import React, { useState, useEffect } from "react";

import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import { notify } from "../../../../services/toast.config";
import { toProperCase } from "../../../../helper/company_helper";

interface MarkProductLostModalProps {
  selectedLostProduct: any;
  setSelectedLostProduct: any;
  selectedCompany: any;
  refreshProductList: any;
}

const MarkProductLostModal: React.FC<MarkProductLostModalProps> = ({
  selectedLostProduct,
  setSelectedLostProduct,
  selectedCompany,
  refreshProductList,
}) => {
  const signature = localStorage.getItem("signature");

  const isRecoverMode = selectedLostProduct?.status === "Lost";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkError, setRemarkError] = useState("");

  useEffect(() => {
    setRemark("");
    setRemarkError("");
  }, [selectedLostProduct?.id]);

  const handleModalClose = () => {
    setRemark("");
    setRemarkError("");
    setSelectedLostProduct(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedRemark = remark.trim();

    if (!isRecoverMode && !trimmedRemark) {
      setRemarkError("Remark is required to mark a product as lost.");
      return;
    }

    try {
      setIsSubmitting(true);

      const endpoint = isRecoverMode
        ? "userService/recoverProduct"
        : "userService/markProductLost";

      const response = await api.post(endpoint, {
        signature,
        id: selectedLostProduct?.id,
        company_id: parseInt(selectedCompany?.company_id),
        company_type: selectedCompany?.company_type,
        remark: trimmedRemark,
      });

      notify.success(
        response.data.message?.[0]?.msg ||
          (isRecoverMode
            ? "Product recovered successfully."
            : "Product marked as lost successfully.")
      );

      const dismissButton = document.querySelector(
        "#dismiss-mark-product-lost-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
      handleModalClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message?.[0]?.msg ||
        error?.data?.message?.[0]?.msg ||
        "An error occurred.";
      notify.error(errorMessage);
    } finally {
      await refreshProductList();
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && <Loader />}
      <div className="modal fade" id="mark_product_lost_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                <i
                  className={`ti ${
                    isRecoverMode
                      ? "ti-refresh-dot text-success"
                      : "ti-alert-triangle text-warning"
                  } me-2`}
                />
                {isRecoverMode ? "Recover Product" : "Mark Product as Lost"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleModalClose}
                disabled={isSubmitting}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body pb-0">
                {selectedLostProduct && (
                  <>
                    <div
                      className={`alert ${
                        isRecoverMode ? "alert-success" : "alert-warning"
                      } d-flex align-items-center`}
                    >
                      <i
                        className={`ti ${
                          isRecoverMode ? "ti-info-circle" : "ti-alert-triangle"
                        } me-2 fs-16`}
                      />
                      <span>
                        {isRecoverMode
                          ? "This product will be recovered and set back to Active. Any linked asset will be restored too."
                          : "This product will be marked as lost. Any linked asset (assigned or in stock) will also be marked lost."}
                      </span>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Product:</span>
                        <span className="fw-medium">
                          {toProperCase(selectedLostProduct?.name)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between py-1 border-bottom">
                        <span className="text-muted">Serial Number:</span>
                        <span className="fw-medium">
                          {selectedLostProduct?.serial_no?.toUpperCase() ||
                            "N/A"}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-muted">Current Status:</span>
                        <span className="fw-medium">
                          {selectedLostProduct?.status}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Remark{" "}
                        {isRecoverMode ? (
                          <span className="text-muted">(optional)</span>
                        ) : (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      <textarea
                        className={`form-control ${
                          remarkError ? "is-invalid border-danger" : ""
                        }`}
                        placeholder={
                          isRecoverMode
                            ? "How/where was the product recovered..."
                            : "Explain how/where the product was lost..."
                        }
                        rows={3}
                        maxLength={1000}
                        value={remark}
                        onChange={(e) => {
                          setRemark(e.target.value);
                          if (remarkError) setRemarkError("");
                        }}
                        disabled={isSubmitting}
                      />
                      {remarkError && (
                        <div className="text-danger mt-1">
                          <small>{remarkError}</small>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn ${
                    isRecoverMode ? "btn-success" : "btn-warning"
                  } me-2`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      {isRecoverMode ? "Recovering..." : "Marking..."}
                    </>
                  ) : isRecoverMode ? (
                    "Recover Product"
                  ) : (
                    "Mark as Lost"
                  )}
                </button>

                <button
                  id="dismiss-mark-product-lost-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarkProductLostModal;
